/**
 * 複数企画を管理するマネージャークラス
 */
class KikakuPageMultiManager {
  static modalOverlay = null;
  static confirmationPopup = null;
  static instances = new Map();
  static currentActiveInstance = null;

  /**
   * 静的初期化メソッド
   */
  static init() {
    this.createSharedModal();

    const configElements = document.querySelectorAll('[data-store-stamp-rally-config]');

    configElements.forEach((configElement) => {
      const blockId = configElement.dataset.storeStampRallyConfig;
      const container = document.querySelector(`[data-store-stamp-rally-content="${blockId}"]`);

      if (!container) {
        return;
      }

      const instance = new KikakuPageManager(blockId, container, configElement);
      this.instances.set(blockId, instance);
    });

    this.setupSharedEventListeners();
  }

  /**
   * 共有モーダル要素を作成
   */
  static createSharedModal() {
    if (!this.modalOverlay) {
      this.modalOverlay = document.createElement('div');
      this.modalOverlay.className = 'modal-overlay';
      document.body.appendChild(this.modalOverlay);
    }

    if (!this.confirmationPopup) {
      this.confirmationPopup = document.createElement('div');
      this.confirmationPopup.className = 'confirmation-popup';
      this.confirmationPopup.innerHTML = `
        <button class="confirmation-popup__close" aria-label="閉じる"></button>
        <div class="confirmation-popup__product">
          <div class="confirmation-popup__product-image"></div>
          <div class="confirmation-popup__product-title"></div>
          <div class="confirmation-popup__tag-lead"></div>
        </div>
        <div class="confirmation-popup__message"></div>
        <div class="confirmation-popup__buttons"></div>
      `;
      document.body.appendChild(this.confirmationPopup);

      const closeButton = this.confirmationPopup.querySelector('.confirmation-popup__close');
      closeButton.addEventListener('click', () => {
        this.hideConfirmationPopup();
      });
    }
  }

  /**
   * 共有イベントリスナーを設定
   */
  static setupSharedEventListeners() {
    this.modalOverlay.addEventListener('click', () => {
      this.hideConfirmationPopup();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.hideConfirmationPopup();
      }
    });
  }

  /**
   * 確認ポップアップを表示
   */
  static showConfirmationPopup(instance, itemElement, metaobjectId) {
    this.currentActiveInstance = instance;

    const productTitle = itemElement.querySelector('.store-stamp-rally-item__title')?.textContent || '商品';
    const productImage = itemElement.querySelector('.store-stamp-rally-item__image img');
    const productTagline = itemElement.querySelector('.store-stamp-rally-item__tagline')?.textContent || '';

    const popupImage = this.confirmationPopup.querySelector('.confirmation-popup__product-image');
    const popupTitle = this.confirmationPopup.querySelector('.confirmation-popup__product-title');
    const popupTagLead = this.confirmationPopup.querySelector('.confirmation-popup__tag-lead');
    const popupMessage = this.confirmationPopup.querySelector('.confirmation-popup__message');
    const popupButtons = this.confirmationPopup.querySelector('.confirmation-popup__buttons');

    if (productImage) {
      popupImage.innerHTML = `<img src="${productImage.src}" alt="${productImage.alt}" width="56" height="56">`;
    } else {
      popupImage.innerHTML = '';
    }

    popupTitle.textContent = productTitle;

    if (productTagline.length > 0) {
      popupTagLead.textContent = productTagline;
      this.confirmationPopup.classList.add('confirmation-popup--has-tag-lead');
    } else {
      popupTagLead.textContent = '';
      this.confirmationPopup.classList.remove('confirmation-popup--has-tag-lead');
    }

    popupMessage.innerHTML = 'スタッフに画面を提示ください';

    popupButtons.innerHTML = `
      <button class="confirmation-popup__button confirmation-popup__button--visit" data-action="visit">
        来店
      </button>
    `;

    this.setupPopupButtonListeners(instance, metaobjectId);

    this.modalOverlay.classList.add('active');
    this.confirmationPopup.classList.add('active');
  }

  /**
   * ポップアップボタンのイベントリスナーを設定
   */
  static setupPopupButtonListeners(instance, metaobjectId) {
    const buttons = this.confirmationPopup.querySelectorAll('.confirmation-popup__button');

    buttons.forEach((button) => {
      button.replaceWith(button.cloneNode(true));
    });

    const newButtons = this.confirmationPopup.querySelectorAll('.confirmation-popup__button');

    newButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const action = event.currentTarget.getAttribute('data-action');

        if (action === 'visit') {
          instance.handleVisit(metaobjectId);
        }
      });
    });
  }

  /**
   * ポップアップボタンを無効化
   */
  static disablePopupButtons() {
    const buttons = this.confirmationPopup.querySelectorAll('.confirmation-popup__button');

    buttons.forEach((button) => {
      button.disabled = true;
      button.dataset.originalText = button.textContent;
      button.textContent = '処理中...';
    });
  }

  /**
   * ポップアップボタンを有効化
   */
  static enablePopupButtons() {
    const buttons = this.confirmationPopup.querySelectorAll('.confirmation-popup__button');

    buttons.forEach((button) => {
      button.disabled = false;

      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
      }
    });
  }

  /**
   * 確認ポップアップを非表示
   */
  static hideConfirmationPopup() {
    if (!this.confirmationPopup || !this.modalOverlay) {
      return;
    }

    this.confirmationPopup.classList.remove('active');
    this.modalOverlay.classList.remove('active');
    this.currentActiveInstance = null;
  }
}

/**
 * 個別企画を管理するクラス
 */
class KikakuPageManager {
  constructor(blockId, container, configElement) {
    this.blockId = blockId;
    this.container = container;
    this.customerId = null;
    this.objectType = null;
    this.defaultUrl = null;
    this.dailyPostCountLimit = null;
    this.linkedKikakuList = null;
    this.visitButtons = [];

    this.init(configElement);
  }

  /**
   * 初期化処理
   */
  init(configElement) {
    this.loadConfig(configElement);
    this.visitButtons = this.container.querySelectorAll('.store-stamp-rally-item__visit-btn');
    this.setupEventListeners();
  }

  /**
   * 設定を取得
   */
  loadConfig(configElement) {
    if (!configElement) {
      return;
    }

    try {
      const config = JSON.parse(configElement.textContent);
      this.customerId = config.customerId;
      this.objectType = config.objectType;
      this.defaultUrl = config.defaultUrl;
      this.dailyPostCountLimit = config.dailyPostCountLimit;
      this.linkedKikakuList = config.linkedKikakuList || [];
    } catch (error) {
      console.error(`KikakuPageManager[${this.blockId}]: 設定の読み込みに失敗`, error);
    }
  }

  /**
   * イベントリスナーを設定
   */
  setupEventListeners() {
    if (!this.customerId) {
      return;
    }

    this.visitButtons.forEach((button) => {
      if (button.disabled) {
        return;
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const itemElement = button.closest('.store-stamp-rally-item');
        const metaobjectId = button.dataset.postedMetaobjectId;

        if (!itemElement || !metaobjectId) {
          return;
        }

        this.objectType = button.dataset.metaobjectType || this.objectType;
        this.dailyPostCountLimit = Number(button.dataset.dailyPostCountLimit || 0);
        this.linkedKikakuList = this.parseLinkedKikakuList(button.dataset.linkedKikakuList);

        KikakuPageMultiManager.showConfirmationPopup(this, itemElement, metaobjectId);
      });
    });
  }

  /**
   * 連動企画リストを取得
   * @param {string} linkedKikakuListJson - JSON形式の連動企画リスト
   * @returns {Array<string>} 連動企画リスト
   */
  parseLinkedKikakuList(linkedKikakuListJson) {
    if (!linkedKikakuListJson) {
      return [];
    }

    try {
      const linkedKikakuList = JSON.parse(linkedKikakuListJson);
      return Array.isArray(linkedKikakuList) ? linkedKikakuList : [];
    } catch (error) {
      console.error(`KikakuPageManager[${this.blockId}]: linkedKikakuListの読み込みに失敗`, error);
      return [];
    }
  }

  /**
   * 今日の日付を取得（YYYY-MM-DD形式）
   * @returns {string} 今日の日付
   */
  getTodayString() {
    const now = new Date();

    return now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0');
  }

  /**
   * 連動企画の制限情報用LocalStorageキーを生成
   * @returns {string} LocalStorageキー
   */
  getLinkedKikakuLimitKey() {
    return `linked_kikaku_limits_${this.customerId}`;
  }

  /**
   * 連動企画の制限情報を取得（配列形式）
   * @returns {Array} 制限情報の配列
   */
  getLinkedKikakuLimitData() {
    const key = this.getLinkedKikakuLimitKey();
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  }

  /**
   * 連動企画の制限情報を保存（配列形式）
   * @param {Array} limitDataArray - 制限情報の配列
   */
  saveLinkedKikakuLimitData(limitDataArray) {
    const key = this.getLinkedKikakuLimitKey();
    localStorage.setItem(key, JSON.stringify(limitDataArray));
  }

  /**
   * 古い日付の制限定義を削除
   */
  cleanupOldLimitData() {
    const today = this.getTodayString();
    const dataArray = this.getLinkedKikakuLimitData();

    if (dataArray.length === 0) {
      return;
    }

    const todayData = dataArray.filter((data) => data.日付 === today);

    if (todayData.length !== dataArray.length) {
      this.saveLinkedKikakuLimitData(todayData);
    }
  }

  /**
   * 現在の企画が連動企画に含まれているかチェック
   * @param {Array<string>} linkedKikakuList - 連動企画リスト
   * @returns {boolean} 含まれているかどうか
   */
  isCurrentKikakuInLinkedList(linkedKikakuList) {
    if (!Array.isArray(linkedKikakuList)) {
      return false;
    }

    return linkedKikakuList.includes(this.objectType);
  }

  /**
   * 連動企画の制限情報を取得または作成
   * @returns {Object|null} 制限情報
   */
  getOrCreateLinkedKikakuLimit() {
    const today = this.getTodayString();

    this.cleanupOldLimitData();

    const dataArray = this.getLinkedKikakuLimitData();
    let linkedKikakuList = this.linkedKikakuList || [];

    if (!linkedKikakuList.includes(this.objectType)) {
      linkedKikakuList = [this.objectType, ...linkedKikakuList];
    }

    const groupId = linkedKikakuList.slice().sort().join(',');

    let limitData = dataArray.find((data) => {
      const existingGroupId = data.連動する企画.slice().sort().join(',');
      return existingGroupId === groupId && data.日付 === today;
    });

    if (limitData) {
      return limitData;
    }

    const dailyLimit = this.dailyPostCountLimit || 0;

    if (dailyLimit <= 0) {
      return null;
    }

    limitData = {
      "日付": today,
      "連動する企画": linkedKikakuList,
      "1日あたりの制限数": dailyLimit,
      "本日の閲覧数": 0,
      "制限適用": true
    };

    dataArray.push(limitData);
    this.saveLinkedKikakuLimitData(dataArray);

    return limitData;
  }

  /**
   * 投稿制限チェック（連動企画対応）
   * @param {string} metaobjectId - チェック対象のmetaobject-id
   * @returns {boolean} 投稿可能かどうか
   */
  canPost(metaobjectId) {
    const limitData = this.getOrCreateLinkedKikakuLimit();

    if (!limitData || !limitData.制限適用 || limitData['1日あたりの制限数'] <= 0) {
      return true;
    }

    if (!this.isCurrentKikakuInLinkedList(limitData.連動する企画)) {
      return true;
    }

    const metaobjectIdString = metaobjectId.toString();

    if (this.isPostedTodayInLinkedKikaku(metaobjectIdString, limitData)) {
      return true;
    }

    return limitData['本日の閲覧数'] < limitData['1日あたりの制限数'];
  }

  /**
   * 連動企画でアイテムが今日投稿済みかチェック
   * @param {string} metaobjectId - チェック対象のmetaobject-id
   * @param {Object|null} limitData - 制限情報
   * @returns {boolean} 今日投稿済みかどうか
   */
  isPostedTodayInLinkedKikaku(metaobjectId, limitData) {
    if (!limitData || !limitData.連動する企画) {
      return false;
    }

    for (const kikakuType of limitData.連動する企画) {
      const key = `kikaku_daily_post_items_${this.customerId}_${limitData.日付}_${kikakuType}`;
      const items = localStorage.getItem(key);

      if (!items) {
        continue;
      }

      const itemList = JSON.parse(items);

      if (itemList.includes(metaobjectId)) {
        return true;
      }
    }

    return false;
  }

  /**
   * 連動企画で投稿したアイテムIDを追加
   * @param {string} metaobjectId - 投稿したアイテムのmetaobject-id
   */
  addTodayPostedItemToLinkedKikaku(metaobjectId) {
    const limitData = this.getOrCreateLinkedKikakuLimit();

    if (!limitData || !this.isCurrentKikakuInLinkedList(limitData.連動する企画)) {
      return;
    }

    const metaobjectIdString = metaobjectId.toString();

    if (this.isPostedTodayInLinkedKikaku(metaobjectIdString, limitData)) {
      return;
    }

    const key = `kikaku_daily_post_items_${this.customerId}_${limitData.日付}_${this.objectType}`;
    const currentItems = JSON.parse(localStorage.getItem(key) || '[]');
    currentItems.push(metaobjectIdString);
    localStorage.setItem(key, JSON.stringify(currentItems));

    limitData['本日の閲覧数'] += 1;

    const dataArray = this.getLinkedKikakuLimitData();
    const groupId = limitData.連動する企画.slice().sort().join(',');
    const index = dataArray.findIndex((data) => {
      const existingGroupId = data.連動する企画.slice().sort().join(',');
      return existingGroupId === groupId && data.日付 === limitData.日付;
    });

    if (index !== -1) {
      dataArray[index] = limitData;
      this.saveLinkedKikakuLimitData(dataArray);
    }
  }

  /**
   * 制限到達時のメッセージを取得（連動企画対応）
   * @returns {string} 制限到達メッセージ
   */
  getLimitReachedMessage() {
    const limitData = this.getOrCreateLinkedKikakuLimit();

    if (!limitData) {
      return '来店記録の制限情報を取得できませんでした。';
    }

    return `連動企画全体で1日の来店記録上限（${limitData['1日あたりの制限数']}件）に達しています。現在${limitData['本日の閲覧数']}件記録済みです。明日以降にお試しください。`;
  }

  /**
   * 来店記録を送信
   * @param {string} metaobjectId - 投稿アイテムID
   */
  async handleVisit(metaobjectId) {
    if (!this.customerId) {
      alert('回答機能を使用するにはログインが必要です。');
      return;
    }

    if (!this.objectType) {
      alert('来店記録の送信に必要な企画情報を取得できませんでした。');
      return;
    }

    if (!this.canPost(metaobjectId)) {
      alert(this.getLimitReachedMessage());
      return;
    }

    KikakuPageMultiManager.disablePopupButtons();

    const button = this.container.querySelector(`.store-stamp-rally-item__visit-btn[data-posted-metaobject-id="${metaobjectId}"]`);

    if (!button) {
      KikakuPageMultiManager.enablePopupButtons();
      return;
    }

    try {
      const response = await fetch(this.defaultUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'customer_id': this.customerId,
          'metaobject_type': this.objectType,
          'target_type': 'metaobject',
          'value': metaobjectId,
          'action': 'like',
          'is_cancel': 'register'
        })
      });

      const result = await response.json();

      if (!response.ok) {
        KikakuPageMultiManager.enablePopupButtons();
        alert('エラーが発生しました: ' + (result.error || '不明なエラー'));
        return;
      }

      const limitData = this.getOrCreateLinkedKikakuLimit();

      if (limitData && !this.isPostedTodayInLinkedKikaku(metaobjectId, limitData)) {
        this.addTodayPostedItemToLinkedKikaku(metaobjectId);
      }

      window.location.reload();
    } catch (error) {
      KikakuPageMultiManager.enablePopupButtons();
      alert('エラーが発生しました。時間をおいて再度お試しください。');
      console.error(`KikakuPageManager[${this.blockId}]: Error`, error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  KikakuPageMultiManager.init();
});
